import { word } from "@/modules/core/utils/i18n"
import type { OnOffProgramProgressionEntity } from "@/modules/programs/entities/onOffProgramProgressionEntity"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { ProgramProgressionStatus } from "@/modules/programs/types/programOnOffTypes"
import { ProgramSubscriptionStatus, ProgramType } from "@/modules/programs/types/programTypes"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { displayedDateFormat } from "@/modules/utils/dateUtils"
import { capitalize } from "@/modules/utils/stringUtils"
import dayjs from "dayjs"

type SessionDetailsContentModel = {
  sessionDate: string | null
  isLastOnOffProgramSession: boolean
  canUserSeeSessionContent: boolean
  canUserEditSession: boolean
  canSeeScoreboardCard: boolean
  canUserAccessToScoreboard: boolean
  canUserCompleteExercisesScore: boolean
  canUserToggleSessionCompleted: boolean
}

export function createSessionDetailsContentModel(
  program: UserProgramDetailsEntity,
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
  progression: OnOffProgramProgressionEntity | null,
  isAuthor: boolean,
  isAcceptedSharedCoach: boolean,
  allowEditContent: boolean,
): SessionDetailsContentModel {
  /* Get the session date */
  function getSessionDate(): string | null {
    if (session.date) {
      if (session instanceof ProgramOnOffSessionEntity && session.index !== null) {
        return `${capitalize(dayjs(session.date).format(displayedDateFormat))} - ${capitalize(word("global.day.label", { count: 1 }))} ${session.index + 1}`
      } else {
        return `${capitalize(dayjs(session.date).format(displayedDateFormat))}`
      }
    } else if (session instanceof ProgramOnOffSessionEntity && session.index !== null) {
      return `${capitalize(word("global.day.label", { count: 1 }))} ${session.index + 1}`
    }
    return null
  }

  /* Get the exercises with score requested */
  function getExercisesWithScoreOrFeedbackRequested(): SessionBlockExerciseEntity[] {
    return session.blocks
      .filter(
        (block) => block.type === SessionBlockType.SESSION_EXERCISE && block.content.scoreSetConfigurations.length > 0,
      )
      .map((block) => block.content as SessionBlockExerciseEntity)
  }

  /* Check if the subscription is active */
  function isSubscriptionActive(): boolean {
    return [
      ProgramSubscriptionStatus.ACTIVE,
      ProgramSubscriptionStatus.ACTIVE_BUNDLE,
      ProgramSubscriptionStatus.INACTIVE_BUNDLE,
      ProgramSubscriptionStatus.PENDING_CANCELLATION,
      ProgramSubscriptionStatus.TRIALING,
      ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION,
    ].includes(program.subscriptionStatus)
  }

  /* Check if all scores are completed */
  function isAllScoresCompleted(): boolean {
    return getExercisesWithScoreOrFeedbackRequested().every((exercise) => {
      // If the score type is TEXT_FROM_ATHLETE, note must be filled
      // Else, the score must be filled, but on the score form, the note can't be filled if the score value is not defined
      return (
        (exercise.score?.valueDecimal !== null && exercise.score?.valueDecimal !== undefined) || !!exercise.score?.note
      )
    })
  }

  /* Check if the user can see the scoreboard card */
  function canSeeScoreboardCard(): boolean {
    return (
      (isAuthor || isAcceptedSharedCoach || canUserSeeSessionContent()) &&
      session.blocks.length > 0 &&
      session.blocks.filter(
        (block) => block.type === SessionBlockType.SESSION_EXERCISE && block.content.scoreSetConfigurations.length > 0,
      ).length > 0 &&
      ((session instanceof ProgramOnOffSessionEntity && !session.restDay) ||
        session instanceof ProgramInfiniteSessionEntity)
    )
  }

  /* Check if the user can access to the scoreboard */
  function canUserAccessToScoreboard(): boolean {
    return !program.scoreboardLocked || (program.scoreboardLocked && isAllScoresCompleted())
  }

  /* Check if the user can complete the exercises score */
  function canUserCompleteExercisesScore(): boolean {
    return (
      ((isSubscriptionActive() || isAuthor || isAcceptedSharedCoach) &&
        (program.type === ProgramType.VIRTUAL ||
          program.type === ProgramType.PRO ||
          (session instanceof ProgramOnOffSessionEntity &&
            session.index !== null &&
            progression &&
            (progression.status === ProgramProgressionStatus.STARTED || progression.currentIndex > session.index)))) ??
      false
    )
  }

  /* Check if the session is the last on off program session */
  function isLastOnOffProgramSession(): boolean {
    return (
      program.type === ProgramType.ONOFF &&
      session instanceof ProgramOnOffSessionEntity &&
      session.index !== null &&
      program.duration === session.index + 1
    )
  }

  /* Check if the user can see the session content */
  function canUserSeeSessionContent(): boolean {
    const sessionHasStarted = session.date && new Date() > new Date(session.date) && session.blocks.length > 0

    return isAuthor || isAcceptedSharedCoach || sessionHasStarted || !session.hideSessionContent
  }

  /* Check if the user can edit the session */
  function canUserEditSession(): boolean {
    return (isAuthor || isAcceptedSharedCoach) && allowEditContent
  }

  /* Check if the user can toggle the session completed */
  function canUserToggleSessionCompleted(): boolean {
    return canUserCompleteExercisesScore() && !(session instanceof ProgramOnOffSessionEntity && session.restDay)
  }

  return {
    sessionDate: getSessionDate(),
    isLastOnOffProgramSession: isLastOnOffProgramSession(),
    canUserSeeSessionContent: canUserSeeSessionContent(),
    canUserEditSession: canUserEditSession(),
    canSeeScoreboardCard: canSeeScoreboardCard(),
    canUserAccessToScoreboard: canUserAccessToScoreboard(),
    canUserCompleteExercisesScore: canUserCompleteExercisesScore(),
    canUserToggleSessionCompleted: canUserToggleSessionCompleted(),
  }
}
